import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";

import { useProduct } from "../../hooks/queries/useProduct";
import type { EntityDetailProps } from "../../types/entityRegistry";
import type { MainEntity } from "../../types/entityTypes";
import { useExportStage } from "../../utils/export/ExportStage";
import type { ExportOption } from "../common/ExportMenu";
import DetailSections from "../Detail/DetailSections";
import { EntityNavigationProvider } from "../Detail/EntityLink";
import { SourceJumpProvider } from "../Detail/sourceJump";
import EntityFunctionalHeader from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { EntityDetailStatus } from "../Layout/EntityDetailStatus";
import ProductHead from "./ProductHead";
import { detailName } from "./productRelations";
import { productSections } from "./productSections";

// Width (px) the view keeps to on a page of its own
const STANDALONE_WIDTH = 1200;

/**
 * The detail of a product: the registry's Detail component of the type.
 *
 * It fetches the product by its id and shows the overlay's header, the
 * head, and the sections under a strip that stays in view. Loading, not
 * found and graph unavailable show where the existing details show them.
 */
const ProductDetailView: React.FC<EntityDetailProps> = ({
  entityId,
  mode = "overlay",
  onEntityClick,
  onClose,
  scrollContainerRef,
}) => {
  const { product, loading, error } = useProduct(entityId || null);

  const ownScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? ownScrollContainerRef;

  const sections = useMemo(
    () => (product ? productSections(product, mode) : []),
    [product, mode],
  );

  // What the header reads: it shows the type and opens the full page
  const mainEntity = useMemo<MainEntity | null>(
    () =>
      product
        ? {
            id: product.id,
            name: detailName(product),
            entityType: "product",
          }
        : null,
    [product],
  );

  // The printed page: the head and the sections one under the other, as
  // plain tables, with no strip and none of the controls of the screen. The
  // head carries the product's name, so the page's header does not.
  const { print } = useExportStage();
  const exportOptions = useMemo((): ExportOption[] => {
    if (!product) {
      return [];
    }
    return [
      {
        key: "pdf",
        label: "PDF",
        run: () =>
          print(
            <EntityNavigationProvider onEntityClick={onEntityClick}>
              <ProductHead product={product} print />
              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3.5 }}>
                {productSections(product, mode, true)
                  .filter(({ content }) => content)
                  .map((section) => (
                    <Box key={section.id} component="section">
                      {!section.hideHeading && (
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            mb: 1.25,
                            pb: 0.5,
                            fontSize: "1.0625rem",
                            fontWeight: 600,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          {section.heading ?? section.label}
                        </Typography>
                      )}
                      {section.content}
                    </Box>
                  ))}
              </Box>
            </EntityNavigationProvider>,
            {
              title: detailName(product),
              generatedAt: new Date(),
              source: "Alpha Affinity Market Intelligence",
            },
            { titled: true },
          ),
      },
    ];
  }, [product, mode, onEntityClick, print]);

  // Another product starts at its top
  const productId = product?.id;
  useEffect(() => {
    if (productId && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [productId, scrollRef]);

  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  if (!product || !mainEntity) {
    return null;
  }

  return (
    <EntityNavigationProvider onEntityClick={onEntityClick}>
      <SourceJumpProvider>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EntityFunctionalHeader
            mainEntity={mainEntity}
            onClose={onClose || (() => {})}
            mode={mode}
            exportOptions={exportOptions}
          />
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                maxWidth: mode === "standalone" ? STANDALONE_WIDTH : "100%",
                mx: "auto",
              }}
            >
              {/* The head stays at the top with the strip, for a jump to
                  any section from anywhere */}
              <DetailSections
                sections={sections}
                scrollContainerRef={scrollRef}
                header={<ProductHead product={product} />}
              />
            </Box>
          </Box>
        </Box>
      </SourceJumpProvider>
    </EntityNavigationProvider>
  );
};

export default ProductDetailView;
